// ─── OperationsBoard — Painel/cockpit de Operações (variante mercadoria) ──────
//
// Landing do menu Operações quando o módulo `ops_board` está ativo (perfil
// P_Casais). Fase 1: faixa de estados das OT + card "Precisa de atenção"
// (atrasadas / sem veículo) com mensagem rápida ao motorista (ChatDriverDialog).
// Novo UI claro/escuro (tokens --surf-* + identidade ecooGPS), gated à variante.
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Box, Typography, CircularProgress, Button, Tooltip, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import DashboardIcon from '@material-ui/icons/Dashboard';
import WarningAmberIcon from '@material-ui/icons/ReportProblem';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import ChatIcon from '@material-ui/icons/Chat';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { useDispatch } from 'react-redux';
import { AuthContext } from '../../App';
import { setActiveView, setPendingOrderTimelineId } from '../../store/slices/uiSlice';
import { usePlannerTheme, usePlannerToolbarTheme, brandGradient, ON_BRAND } from '../../theme/plannerTheme';
import ChatDriverDialog from '../Orders/ChatDriverDialog';

const STATUS = {
  pending: { label: 'Por atribuir', color: '#64748b' },
  assigned: { label: 'Atribuída', color: '#3b82f6' },
  in_transit: { label: 'Em trânsito', color: '#f59e0b' },
  delivered: { label: 'Entregue', color: '#22c55e' },
  cancelled: { label: 'Cancelada', color: '#ef4444' },
};
const STATUS_ORDER = ['pending', 'assigned', 'in_transit', 'delivered', 'cancelled'];

const ROUTE_STATUS = {
  planned: { label: 'Planeada', color: '#3b82f6', bg: 'rgba(59,130,246,0.14)' },
  in_progress: { label: 'Em curso', color: '#f59e0b', bg: 'rgba(245,158,11,0.16)' },
};

const useStyles = makeStyles(() => ({
  root: {
    width: '100%', height: '100%', overflow: 'auto',
    background: 'var(--surf-page, #f4f8fb)', color: 'var(--surf-text, #1e2f3a)',
    padding: 24,
  },
  header: { display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 },
  headerIcon: {
    width: 46, height: 46, borderRadius: 13, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: ON_BRAND,
    boxShadow: '0 8px 20px rgba(24,189,238,0.38)',
  },
  title: { fontSize: '1.3rem', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--surf-title, #0f2733)' },
  subtitle: { fontSize: '0.8rem', color: 'var(--surf-muted, #5b7384)', marginTop: 1 },
  strip: { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 22 },
  tile: {
    flex: '1 1 130px', minWidth: 120,
    background: 'var(--surf-card, #fff)', border: '1px solid var(--surf-border, #e5eef3)',
    borderRadius: 14, padding: '14px 16px', boxShadow: 'var(--surf-shadow, 0 10px 30px rgba(15,39,51,0.06))',
  },
  tileVal: { fontSize: '1.7rem', fontWeight: 800, lineHeight: 1, letterSpacing: '-0.02em' },
  tileLabel: { fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--surf-muted, #5b7384)', marginTop: 6 },
  card: {
    background: 'var(--surf-card, #fff)', border: '1px solid var(--surf-border, #e5eef3)',
    borderRadius: 16, overflow: 'hidden', boxShadow: 'var(--surf-shadow, 0 12px 34px rgba(15,39,51,0.07))',
  },
  cardHead: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '16px 18px',
    borderBottom: '1px solid var(--surf-border-soft, #eef4f8)',
  },
  row: {
    display: 'flex', alignItems: 'center', gap: 14, padding: '13px 18px',
    borderBottom: '1px solid var(--surf-border-soft, #eef4f8)', flexWrap: 'wrap',
  },
  empty: { padding: '48px 24px', textAlign: 'center', color: 'var(--surf-muted, #5b7384)' },
}));

function fmtRoute(o) {
  const a = o.pickupName || 'Origem';
  const b = o.deliveryName || 'Destino';
  return `${a} → ${b}`;
}

const MONTHS_PT = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
function fmtEur(v) {
  return `${(Number(v) || 0).toLocaleString('pt-PT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €`;
}

export default function OperationsBoard({ lang = 'pt' }) {
  const classes = useStyles();
  const pt = lang === 'pt';
  const theme = usePlannerTheme();
  const newUI = usePlannerToolbarTheme() !== 'classic';
  const surfTheme = newUI ? theme : undefined;
  const grad = brandGradient(theme);
  const dark = theme === 'dark';
  const { token } = useContext(AuthContext);
  const dispatch = useDispatch();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chat, setChat] = useState(null); // { driverId, driverName, activeOT }

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    fetch('/planner-api/transport-orders/ops-board', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setData(null); setLoading(false); });
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const byStatus = data?.byStatus || {};
  const attention = data?.attention || [];
  const routes = data?.routes || [];

  return (
    <Box className={classes.root} data-planner-theme={surfTheme}>
      <Box className={classes.header}>
        <Box className={classes.headerIcon} style={{ background: newUI ? grad : 'linear-gradient(135deg,#0369a1,#0ea5e9)' }}>
          <DashboardIcon />
        </Box>
        <Box style={{ flex: 1 }}>
          <Typography className={classes.title}>{pt ? 'Painel de Operações' : 'Operations Board'}</Typography>
          <Typography className={classes.subtitle}>{pt ? 'Estado das ordens e o que precisa da tua atenção agora' : 'Order states and what needs attention now'}</Typography>
        </Box>
        <Tooltip title={pt ? 'Recarregar' : 'Refresh'}>
          <IconButton size="small" onClick={load} disabled={loading} style={{ color: 'var(--surf-muted, #5b7384)' }}><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      {loading && !data ? (
        <Box className={classes.empty}><CircularProgress /></Box>
      ) : (
        <>
          {/* Faixa de estados das OT (últimos 14 dias) + alertas */}
          <Box className={classes.strip}>
            {STATUS_ORDER.map(k => (
              <Box key={k} className={classes.tile}>
                <Typography className={classes.tileVal} style={{ color: STATUS[k].color }}>{byStatus[k] || 0}</Typography>
                <Typography className={classes.tileLabel}>{STATUS[k].label}</Typography>
              </Box>
            ))}
            <Box className={classes.tile} style={{ borderColor: (data?.delayed ? 'rgba(239,68,68,0.5)' : undefined) }}>
              <Typography className={classes.tileVal} style={{ color: '#ef4444' }}>{data?.delayed || 0}</Typography>
              <Typography className={classes.tileLabel}>{pt ? 'Atrasadas' : 'Late'}</Typography>
            </Box>
            <Box className={classes.tile} style={{ borderColor: (data?.noVehicle ? 'rgba(245,158,11,0.5)' : undefined) }}>
              <Typography className={classes.tileVal} style={{ color: '#f59e0b' }}>{data?.noVehicle || 0}</Typography>
              <Typography className={classes.tileLabel}>{pt ? 'Sem veículo' : 'No vehicle'}</Typography>
            </Box>
          </Box>

          {/* Barra segmentada da distribuição por estado (últimos 14 dias) */}
          {(() => {
            const total = STATUS_ORDER.reduce((s, k) => s + (byStatus[k] || 0), 0);
            if (!total) return null;
            return (
              <Box style={{ marginBottom: 22 }}>
                <Typography style={{ fontSize: '0.74rem', fontWeight: 700, color: 'var(--surf-muted, #5b7384)', marginBottom: 8 }}>
                  {pt ? `Distribuição das OT · últimos 14 dias (${total})` : `OT distribution · last 14 days (${total})`}
                </Typography>
                <Box style={{ display: 'flex', height: 12, borderRadius: 999, overflow: 'hidden', border: '1px solid var(--surf-border, #e5eef3)' }}>
                  {STATUS_ORDER.map(k => {
                    const v = byStatus[k] || 0;
                    if (!v) return null;
                    return <Box key={k} title={`${STATUS[k].label}: ${v}`} style={{ width: `${(v / total) * 100}%`, background: STATUS[k].color }} />;
                  })}
                </Box>
                <Box style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 9 }}>
                  {STATUS_ORDER.filter(k => byStatus[k]).map(k => (
                    <Box key={k} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 9, height: 9, borderRadius: '50%', background: STATUS[k].color }} />
                      <Typography style={{ fontSize: '0.72rem', color: 'var(--surf-muted, #5b7384)' }}>{STATUS[k].label} <b style={{ color: 'var(--surf-title, #0f2733)' }}>{byStatus[k]}</b></Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })()}

          {/* Custos de transporte do mês (dados reais: horas × valor/hora viatura) */}
          {data?.costs && data.costs.monthTotal > 0 && (() => {
            const c = data.costs;
            const monthLabel = (pt ? MONTHS_PT[c.month] : new Date(c.year, c.month, 1).toLocaleDateString('en', { month: 'long' }));
            const maxV = Math.max(1, ...c.topVehicles.map(t => t.cost));
            return (
              <Box className={classes.card} style={{ marginBottom: 22 }}>
                <Box style={{ padding: '18px 20px' }}>
                  <Box style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <Typography style={{ fontSize: '0.88rem', fontWeight: 800, color: 'var(--surf-title, #0f2733)' }}>
                      {pt ? `Custos de transporte · ${monthLabel}` : `Transport costs · ${monthLabel}`}
                    </Typography>
                    <Typography style={{ fontSize: '0.74rem', color: 'var(--surf-muted, #5b7384)' }}>
                      {pt ? `${fmtEur(c.avgPerDay)}/dia em média` : `${fmtEur(c.avgPerDay)}/day avg`}
                    </Typography>
                  </Box>
                  <Box style={{ display: 'flex', alignItems: 'flex-end', gap: 10, marginTop: 8 }}>
                    <Typography style={{ fontSize: '2rem', fontWeight: 800, lineHeight: 1, letterSpacing: '-0.02em', color: 'var(--surf-title, #0f2733)' }}>{fmtEur(c.projected)}</Typography>
                    <Typography style={{ fontSize: '0.78rem', color: 'var(--surf-muted, #5b7384)', marginBottom: 3 }}>{pt ? 'previstos no fim do mês' : 'projected end of month'}</Typography>
                  </Box>
                  <Typography style={{ fontSize: '0.76rem', color: 'var(--surf-muted, #5b7384)', marginTop: 5 }}>
                    <b style={{ color: 'var(--surf-strong, #0f2733)' }}>{fmtEur(c.monthTotal)}</b> {pt ? `até hoje · dia ${c.daysElapsed} de ${c.daysInMonth}` : `so far · day ${c.daysElapsed} of ${c.daysInMonth}`}
                  </Typography>
                </Box>
                {c.topVehicles.length > 0 && (
                  <Box style={{ borderTop: '1px solid var(--surf-border-soft, #eef4f8)', padding: '13px 20px 16px' }}>
                    <Typography style={{ fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--surf-muted, #5b7384)', marginBottom: 10 }}>{pt ? 'Por viatura' : 'By vehicle'}</Typography>
                    {c.topVehicles.map(t => (
                      <Box key={t.plate} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 9 }}>
                        <Typography style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--surf-text, #334155)', width: 96, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.plate}</Typography>
                        <Box style={{ flex: 1, height: 8, borderRadius: 999, background: 'var(--surf-head-bg, #f1f6f9)', overflow: 'hidden' }}>
                          <Box style={{ width: `${(t.cost / maxV) * 100}%`, height: '100%', background: grad }} />
                        </Box>
                        <Typography style={{ fontSize: '0.78rem', fontWeight: 800, color: 'var(--surf-title, #0f2733)', width: 78, textAlign: 'right', flexShrink: 0 }}>{fmtEur(t.cost)}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })()}

          {/* Precisa de atenção */}
          <Box className={classes.card}>
            <Box className={classes.cardHead}>
              <Box style={{ width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(239,68,68,0.14)', color: '#ef4444' }}>
                <WarningAmberIcon style={{ fontSize: 20 }} />
              </Box>
              <Box style={{ flex: 1 }}>
                <Typography style={{ fontSize: '0.98rem', fontWeight: 800, color: 'var(--surf-title, #0f2733)' }}>{pt ? 'Precisa de atenção' : 'Needs attention'}</Typography>
                <Typography style={{ fontSize: '0.76rem', color: 'var(--surf-muted, #5b7384)' }}>{pt ? 'OTs atrasadas ou sem veículo — resolve na própria linha' : 'Late or unassigned OTs'}</Typography>
              </Box>
              <Box style={{ fontSize: '0.8rem', fontWeight: 800, color: '#ef4444' }}>{attention.length}</Box>
            </Box>

            {attention.length === 0 ? (
              <Box className={classes.empty}>
                <LocalShippingIcon style={{ fontSize: 36, color: 'var(--surf-muted2, #94a3b8)' }} />
                <Typography style={{ fontWeight: 700, marginTop: 6, color: 'var(--surf-title, #0f2733)' }}>{pt ? 'Tudo em dia 🎉' : 'All clear 🎉'}</Typography>
                <Typography style={{ fontSize: '0.82rem' }}>{pt ? 'Nenhuma OT atrasada nem sem veículo.' : 'No late or unassigned OTs.'}</Typography>
              </Box>
            ) : attention.map(o => {
              const st = STATUS[o.status] || STATUS.pending;
              return (
                <Box key={o.id} className={classes.row}>
                  {/* selo atrasada / sem veículo */}
                  <Box style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    {o._delayed && <span style={{ fontSize: '0.64rem', fontWeight: 800, color: '#ef4444', background: 'rgba(239,68,68,0.14)', borderRadius: 6, padding: '3px 7px', textTransform: 'uppercase' }}>{pt ? 'Atrasada' : 'Late'}</span>}
                    {o._noVehicle && <span style={{ fontSize: '0.64rem', fontWeight: 800, color: '#f59e0b', background: 'rgba(245,158,11,0.16)', borderRadius: 6, padding: '3px 7px', textTransform: 'uppercase' }}>{pt ? 'Sem veículo' : 'No vehicle'}</span>}
                  </Box>
                  <Box style={{ minWidth: 180, flex: 1 }}>
                    <Typography style={{ fontSize: '0.84rem', fontWeight: 800, color: 'var(--surf-title, #0f2733)' }}>
                      {o.orderNumber || `#${o.id}`} <span style={{ fontWeight: 600, color: 'var(--surf-muted, #5b7384)' }}>· {fmtRoute(o)}</span>
                    </Typography>
                    <Typography style={{ fontSize: '0.74rem', color: 'var(--surf-muted, #5b7384)', marginTop: 2 }}>
                      <span style={{ color: st.color, fontWeight: 700 }}>{st.label}</span>
                      {o.vehiclePlate ? ` · ${o.vehiclePlate}` : ''}{o.driverName ? ` · ${o.driverName}` : ''}
                    </Typography>
                  </Box>
                  <Box style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <Tooltip title={o.driverId ? (pt ? 'Mensagem ao motorista' : 'Message driver') : (pt ? 'OT sem motorista' : 'No driver')}>
                      <span>
                        <Button size="small" startIcon={<ChatIcon style={{ fontSize: 16 }} />} disabled={!o.driverId}
                          onClick={() => setChat({ driverId: o.driverId, driverName: o.driverName, activeOT: o })}
                          style={{ textTransform: 'none', fontWeight: 700, fontSize: '0.76rem', borderRadius: 9, border: '1px solid var(--surf-border, #e5eef3)', color: 'var(--surf-text, #334155)', opacity: o.driverId ? 1 : 0.5 }}>
                          {pt ? 'Mensagem' : 'Message'}
                        </Button>
                      </span>
                    </Tooltip>
                    <Button size="small" endIcon={<ArrowForwardIcon style={{ fontSize: 15 }} />}
                      onClick={() => { dispatch(setPendingOrderTimelineId({ id: o.id, orderNumber: o.orderNumber })); dispatch(setActiveView('orders')); }}
                      style={{ textTransform: 'none', fontWeight: 800, fontSize: '0.76rem', borderRadius: 9, background: newUI ? grad : '#0369a1', color: newUI ? ON_BRAND : '#fff', padding: '5px 14px' }}>
                      {pt ? 'Abrir' : 'Open'}
                    </Button>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Rotas em curso — cada card mostra as OTs que lhe deram origem */}
          {routes.length > 0 && (
            <Box style={{ marginTop: 22 }}>
              <Typography style={{ fontSize: '0.98rem', fontWeight: 800, color: 'var(--surf-title, #0f2733)', marginBottom: 12 }}>
                {pt ? 'Rotas em curso' : 'Active routes'} <span style={{ color: 'var(--surf-muted, #5b7384)', fontWeight: 700 }}>· {routes.length}</span>
              </Typography>
              <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))', gap: 14 }}>
                {routes.map(r => {
                  const rs = ROUTE_STATUS[r.status] || ROUTE_STATUS.planned;
                  return (
                    <Box key={r.id} className={classes.card}>
                      <Box className={classes.cardHead} style={{ gap: 10 }}>
                        <Box style={{ width: 34, height: 34, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surf-head-bg, #f1f6f9)', color: 'var(--surf-muted, #5b7384)', flexShrink: 0 }}>
                          <LocalShippingIcon style={{ fontSize: 18 }} />
                        </Box>
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Typography style={{ fontSize: '0.86rem', fontWeight: 800, color: 'var(--surf-title, #0f2733)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {r.name || `Rota ${r.id}`}
                          </Typography>
                          <Typography style={{ fontSize: '0.74rem', color: 'var(--surf-muted, #5b7384)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {r.vehiclePlate || (pt ? 'Sem veículo' : 'No vehicle')}{r.driverName ? ` · ${r.driverName}` : ''} · {r.otCount} OT{r.otCount !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                        <span style={{ fontSize: '0.62rem', fontWeight: 800, color: rs.color, background: rs.bg, borderRadius: 6, padding: '3px 8px', textTransform: 'uppercase', flexShrink: 0 }}>{rs.label}</span>
                        {r.driverId && (
                          <Tooltip title={pt ? 'Mensagem ao motorista' : 'Message driver'}>
                            <IconButton size="small" onClick={() => setChat({ driverId: r.driverId, driverName: r.driverName, activeOT: r.ots[0] || null })} style={{ color: 'var(--surf-muted, #5b7384)', flexShrink: 0 }}>
                              <ChatIcon style={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                      <Box style={{ padding: '5px 0 8px' }}>
                        {r.ots.map(o => {
                          const os = STATUS[o.status] || STATUS.pending;
                          return (
                            <Box key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 18px' }}>
                              <span style={{ width: 7, height: 7, borderRadius: '50%', background: os.color, flexShrink: 0 }} />
                              <Typography style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--surf-text, #334155)', flexShrink: 0 }}>{o.orderNumber || `#${o.id}`}</Typography>
                              <Typography style={{ fontSize: '0.76rem', color: 'var(--surf-muted, #5b7384)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{fmtRoute(o)}</Typography>
                              <span style={{ fontSize: '0.66rem', fontWeight: 700, color: os.color, flexShrink: 0 }}>{os.label}</span>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </>
      )}

      {chat && (
        <ChatDriverDialog open onClose={() => setChat(null)}
          driverId={chat.driverId} driverName={chat.driverName} activeOT={chat.activeOT} />
      )}
    </Box>
  );
}
